import { useEffect, useState } from 'react';

import { Button, Tag, Tabs, Tooltip } from 'antd';

import { HotjarEvents, submitEvent } from 'hotjar';
import BlacklistMessage from 'v2/components/Shared/BlacklistMessage';
import PlgAnticipation from 'v2/components/Shared/PlgAnticipation';
import TopPage from 'v2/components/Shared/TopPage';
import Tour from 'v2/components/Shared/Tour';
import { ToggleButton } from 'v2/components/Shared/ValuesVisibility';
import { sendEventToGA } from 'v2/helpers/analytics';
import { EFinancialStatementTabs } from 'v2/interfaces/financial';
import type { ListOrder } from 'v2/services/financialStatement/statement';

import { Icon } from '@eduzz/ui-antd-theme';

import Blocked from './Blocked';
import CardList from './CardList';
import { tourSteps } from './constants';
import ExportModal from './ExportModal';
import Filter from './Filter';
import Future from './Future';
import Statement from './Statement';

const pageTitle = (
  <span className='inline-flex items-center gap-2'>
    Extrato Financeiro
    <Tag className='m-0 inline-flex h-5 items-center px-2 text-[[0.6875rem] leading-none font-normal uppercase'>
      Wallet BRL
    </Tag>
  </span>
);

function FinancialStatement() {
  const [shouldDisabledSortBtn, setShouldDisabledSortBtn] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<ListOrder>('DESC');
  const [tab, setTab] = useState<EFinancialStatementTabs>(EFinancialStatementTabs.STATEMENT);
  const [exportModalItsOpen, setExportModalItsOpen] = useState(false);

  const showOrderButton = tab !== EFinancialStatementTabs.STATEMENT;
  const currOrderIsAscending = currentOrder === 'ASC';

  const handleChange = (newTab: string) => {
    const translateTabName = (t: EFinancialStatementTabs) => {
      if (t === EFinancialStatementTabs.STATEMENT) return 'Extrato';
      if (t === EFinancialStatementTabs.FUTURE) return 'Lançamentos Futuros';
      return 'Lançamentos Bloqueados';
    };

    sendEventToGA({
      category: 'Novo Extrato',
      action: 'SubAba',
      label: translateTabName(newTab as EFinancialStatementTabs)
    });

    setTab(newTab as EFinancialStatementTabs);
  };

  const handleClickExport = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Exportar'
    });

    setExportModalItsOpen(true);
  };

  const handleToggleExportModal = () => setExportModalItsOpen(prevState => !prevState);
  const handleChangeOrder = () => setCurrentOrder(currOrderIsAscending ? 'DESC' : 'ASC');

  const AdditionalButtons = () => (
    <>
      {showOrderButton && (
        <Tooltip title={`Ordem ${currOrderIsAscending ? '' : 'de'}crescente`} placement='top'>
          <Button
            className='min-w-10'
            color='default'
            variant='filled'
            size='small'
            onClick={handleChangeOrder}
            disabled={shouldDisabledSortBtn}
          >
            {currOrderIsAscending ? <Icon.ArrowDownNarrowWide /> : <Icon.ArrowUpNarrowWide />}
          </Button>
        </Tooltip>
      )}

      <Tooltip title='Exporte e analise seu extrato em períodos de até 12 meses' placement='top'>
        <Button
          color='default'
          variant='filled'
          size='small'
          onClick={handleClickExport}
          icon={<Icon.Download size={16} />}
        >
          Exportar
        </Button>
      </Tooltip>
    </>
  );

  useEffect(() => submitEvent(HotjarEvents.NEW_STATEMENT_ACCESS), []);

  return (
    <div className='font-sans'>
      <BlacklistMessage />

      <TopPage
        title={pageTitle}
        extra='Acompanhe as movimentações e o saldo da sua wallet.'
        additionalContent={<ToggleButton />}
        helpLink='https://ajuda.eduzz.com/hc/pt-br/articles/4402964669467-Extrato-Financeiro-%C3%93rbita'
      />

      <CardList />
      <Filter currentTab={tab} />
      <div id='main-results' className='mt-2'>
        <div className='mb-2 flex flex-wrap justify-between gap-2'>
          <Tabs
            size='middle'
            className='max-sm:w-full'
            activeKey={tab}
            onChange={handleChange}
            items={[
              { key: EFinancialStatementTabs.STATEMENT, label: 'Extrato' },
              { key: EFinancialStatementTabs.FUTURE, label: 'Lançamentos Futuros' },
              { key: EFinancialStatementTabs.BLOCKED, label: 'Lançamentos Bloqueados' }
            ]}
          />

          <div className='mb-4 flex items-center gap-2 max-sm:mx-2 max-sm:mb-2'>
            <AdditionalButtons />
          </div>
        </div>

        {tab === EFinancialStatementTabs.STATEMENT && <Statement />}
        {tab === EFinancialStatementTabs.FUTURE && (
          <Future order={currentOrder} shouldDisableSortBtn={setShouldDisabledSortBtn} />
        )}
        {tab === EFinancialStatementTabs.BLOCKED && (
          <Blocked order={currentOrder} shouldDisableSortBtn={setShouldDisabledSortBtn} />
        )}

        {exportModalItsOpen && <ExportModal isOpen={exportModalItsOpen} setClose={handleToggleExportModal} />}

        <Tour steps={tourSteps} name='financial_statement_tour' />
      </div>

      <PlgAnticipation page='financial-statement' />
    </div>
  );
}

export default FinancialStatement;
